<template>
    <div class="vstack justify-content-center gap-3 p-3 px-4 flex-grow-1">
        <p class="text-center mb-0 py-2" style="font-size: 16px; color: #3B3A3A;">Seret gambar untuk laraskan kedudukan</p>
        <div class="btn-group-media-preview">
            <button
                class="btn btn-tukar-gambar w-100" 
                @click="removeRecorderdVideo">
                <span>Tukar gambar</span> <RefreshCw :size="16" class="ms-1" style="color: #DDB56D" />
            </button>
            <button class="btn btn-tnb-gradient w-100" @click="handleNext">Selesai </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { Check, RefreshCw } from 'lucide-vue-next';
import useFabricCanvas from '../../composables/use-fabric-canvas';
import { useDesignStore, usePreviewStore } from '../../stores';
import { storeToRefs } from 'pinia';

const emits = defineEmits(['back', 'next']);

const previewStore = usePreviewStore();
const { recordStreamCamRef, selectedMediaType } = storeToRefs(previewStore);

const designStore = useDesignStore();

const mediaNames = ['record-stream-picture-element', 'record-stream-video-element'];

let isDragging = false;
let dragTarget: any = null;
let lastPosX = 0;
let lastPosY = 0;

const enableImagePanning = () => {
    const [canvas] = useFabricCanvas();
    // Keep media objects non-selectable — we handle drag manually
    canvas.getObjects().forEach(obj => {
        obj.set({ evented: false, selectable: false });
    });
    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);
    canvas.renderAll();
};

const onMouseDown = (e: any) => {
    const [canvas] = useFabricCanvas();
    const pointer = canvas.getPointer(e.e);
    const hit = canvas.getObjects().find((o: any) =>
        mediaNames.includes(o.name) && o.containsPoint(pointer)
    );
    if (hit) {
        isDragging = true;
        dragTarget = hit;
        lastPosX = pointer.x;
        lastPosY = pointer.y;
    }
};

const onMouseMove = (e: any) => {
    if (!isDragging || !dragTarget) return;
    const [canvas] = useFabricCanvas();
    const pointer = canvas.getPointer(e.e);
    dragTarget.set({
        left: (dragTarget.left ?? 0) + (pointer.x - lastPosX),
        top: (dragTarget.top ?? 0) + (pointer.y - lastPosY),
        opacity: 0.5,
    });
    dragTarget.setCoords();
    lastPosX = pointer.x;
    lastPosY = pointer.y;
    canvas.renderAll();
};

const onMouseUp = () => {
    if (isDragging && dragTarget) {
        const [canvas] = useFabricCanvas();
        dragTarget.set({ opacity: 1 });
        dragTarget.moveTo(0);
        canvas.renderAll();
    }
    isDragging = false;
    dragTarget = null;
};

const disableImagePanning = () => {
    const [canvas] = useFabricCanvas();
    canvas.off('mouse:down', onMouseDown);
    canvas.off('mouse:move', onMouseMove);
    canvas.off('mouse:up', onMouseUp);
    isDragging = false;
    dragTarget = null;
    canvas.getObjects().forEach(obj => {
        obj.set({ evented: true });
    });
    canvas.renderAll();
};

const handleNext = () => {
    disableImagePanning();
    emits('next');
};

const removeRecorderdVideo = async () => {
    disableImagePanning();
    const [canvas] = useFabricCanvas();
    let removeMediaElementList = ['record-stream-video-element', 'record-stream-picture-element'];
    removeMediaElementList.forEach(item => {
        if (canvas.getObjects().find(data => data.name === item)) {
            designStore.removeFabricCanvasElement(item);
        }
    });

    (recordStreamCamRef.value as HTMLVideoElement).pause();
    (recordStreamCamRef.value as HTMLVideoElement).currentTime = 0;

    selectedMediaType.value = null;

    emits('back');
}

onMounted(() => {
    if (recordStreamCamRef.value && selectedMediaType.value === 'video') {
        (recordStreamCamRef.value as HTMLVideoElement).play();
    }
    enableImagePanning();
})

onUnmounted(() => {
    if (recordStreamCamRef.value && selectedMediaType.value === 'video') {
        (recordStreamCamRef.value as HTMLVideoElement).pause();
        (recordStreamCamRef.value as HTMLVideoElement).currentTime = 0;
    }
    disableImagePanning();
})

</script>

<style lang="scss" scoped>
.btn-group-media-preview {
    display: flex;
    flex-direction: column-reverse;
    gap: 0.75rem;
    justify-content: end;

    @media (max-height: 800px) {
        flex-direction: row;
        gap: 0.75rem;
    }

    @media (min-height: 1000px) {
        flex-direction: row;
        gap: 0.75rem;
    }
}

.btn-tukar-gambar {
    background-image: url('/images/svg/white-button.svg');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center center;
    background-color: transparent;
    border: none;
    border-radius: 0;
    white-space: nowrap;
    transition: filter .3s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 700;
    width: 100%;
    min-height: 66px;
    padding: 0.85rem 1rem;
    overflow: visible;

    @media (max-height: 799px) {
        font-size: 12px;
        padding: 0.6rem 1rem;
        min-height: 50px;
    }

    @media (max-height: 700px) {
        font-size: 11px;
        padding: 0.4rem 0.75rem;
        min-height: 40px;
    }

    @media (min-height: 1000px) {
        font-size: 11px;
        padding: 0.4rem 0.75rem;
        min-height: 40px;
    }

    span {
        background: linear-gradient(90deg, #A47020, #DDB56D);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    &:hover,
    &:focus {
        filter: brightness(0.95);
    }
}
</style>